// src/components/admin/flows/components/SLAIndicator.js
import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';

const SLAIndicator = ({ deadline, label = 'SLA Deadline' }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        if (!deadline) return;

        const calc = () => {
            const now = new Date();
            const end = new Date(deadline);
            const diff = end - now;
            setTimeLeft(diff);
        };

        calc();
        const interval = setInterval(calc, 60000); // Update every minute
        return () => clearInterval(interval);
    }, [deadline]);

    if (timeLeft === null) return null;

    const isOverdue = timeLeft < 0;
    const isUrgent = timeLeft > 0 && timeLeft < 2 * 60 * 60 * 1000; // < 2 hours
    const isWarning = timeLeft > 0 && timeLeft < 8 * 60 * 60 * 1000; // < 8 hours

    const absMs = Math.abs(timeLeft);
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));

    const timeString = hours > 24
        ? `${Math.floor(hours / 24)}d ${hours % 24}h`
        : hours > 0
        ? `${hours}h ${minutes}m`
        : `${minutes}m`;

    const statusColor = isOverdue
        ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
        : isUrgent
        ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
        : isWarning
        ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
        : 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';

    const textColor = isOverdue
        ? 'text-red-600 dark:text-red-400'
        : isUrgent
        ? 'text-amber-600 dark:text-amber-400'
        : isWarning
        ? 'text-yellow-600 dark:text-yellow-400'
        : 'text-blue-600 dark:text-blue-400';

    const iconColor = isOverdue || isUrgent
        ? 'text-red-500'
        : isWarning
        ? 'text-amber-500'
        : 'text-blue-500';

    // Progress bar (percentage of SLA elapsed)
    const totalSlaMs = new Date(deadline) - new Date(new Date(deadline).getTime() - 48 * 60 * 60 * 1000); // Assume 48h SLA
    const elapsed = totalSlaMs - timeLeft;
    const progressPercent = Math.min(100, Math.max(0, (elapsed / totalSlaMs) * 100));

    return (
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${statusColor}`}>
            {isOverdue || isUrgent ? (
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
            ) : (
                <Clock className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
            )}
            <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                    <p className={`text-sm font-medium ${textColor}`}>
                        {label}
                    </p>
                    <p className={`text-sm font-bold ${textColor}`}>
                        {isOverdue ? `Overdue by ${timeString}` : `${timeString} remaining`}
                    </p>
                </div>
                {/* Progress bar */}
                <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                        className={`h-full rounded-full transition-all ${
                            isOverdue ? 'bg-red-500' : isUrgent ? 'bg-amber-500' : isWarning ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Due: {new Date(deadline).toLocaleString()}
                </p>
            </div>
        </div>
    );
};

export default SLAIndicator;
